import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import cryptoGift from '../../../assets/images/crypto-gift.svg';
import { useTranslation } from 'react-i18next';
import { useModal, useProfile, useRemainingTime } from '@/hooks';
import { Modal } from '@/components';
import {
  GiveawayHomepageDtoRead,
  useGiveawayControllerJoinMutation,
} from '@/store/services/MidleNestApi.service';
import TicketInGiveawayIcon from './TicketInGiveaway.icon';

type Props = React.PropsWithChildren &
  GiveawayHomepageDtoRead & {
    className?: string;
    isAuthenticated: boolean;
  };

export default function GiveawayBanner({
  title,
  description,
  rewardToken,
  endDate,
  totalReward = 0,
  theme,
  participantCount,
  ticketUsed,
  tag,
  metamaskRequired,
  minXP,
  joinedTickets,
  className = '',
  isAuthenticated,
  ...props
}: Props) {
  const { t } = useTranslation();
  // const { id, showModal, hideModal } = useModal();
  const { id, showModal, hideModal } = useModal(props.id);
  const { profile, reloadProfile } = useProfile();
  const remainingTime = useRemainingTime(endDate);
  const pointGap = isAuthenticated ? Math.max(minXP - Number(profile?.currentXp), 0) : 0;
  // TODO: bunun baska bir yolu kullanilabilir
  const wallet = profile?.user?.accounts?.filter((x) => x.provider === 'wallet')[0];
  // TODO: giveaway katilim sartlarinda wallet var mi kontrol edilmeli
  const isMetamaskConnected = !isAuthenticated || !!wallet || !metamaskRequired;

  const [tickets, setTickets] = useState(
    isAuthenticated ? (!pointGap && profile.currentTicket ? 1 : 0) : 1,
  );

  useEffect(() => {
    setTickets(isAuthenticated ? (!pointGap && profile.currentTicket ? 1 : 0) : 1);
  }, [profile, isAuthenticated]);

  const [mutation] = useGiveawayControllerJoinMutation();

  const [usage, setUsage] = useState(joinedTickets);

  const handleEnter = useCallback(() => {
    // mutation({ giveawayJoinDto: { tickets }, id: props.id })
    mutation({ giveawayJoinDto: { tickets }, id: props.id })
      .unwrap()
      .then(async () => {
        // update the ticket  usage
        await reloadProfile();
        setUsage(tickets);
        gtag('event', 'giveaway-join', { user: profile.id });
        hideModal();
      });
  }, [tickets, profile, reloadProfile, mutation, props.id, hideModal]);

  // const handleUp = () => {
  //   setTickets(Math.min(profile?.currentTicket || 0, tickets + 1));
  // };
  // const handleMax = () => {
  //   setTickets(Number(profile?.currentTicket));
  // };
  // const handleDown = () => {
  //   setTickets(Math.max(tickets - 1, profile?.currentTicket ? 1 : 0));
  // };

  const isAvailable = !!(!tickets || pointGap || !isMetamaskConnected || !remainingTime.time);

  return (
    <>
      <div
        style={{
          backgroundImage: `url(${tag === 'left' ? '/giveaway-left.png' : '/giveaway-right.png'})`,
          backgroundSize: 'cover',
        }}
        className={classNames(
          'relative max-w-full flex flex-col items-center lg:flex-row px-7 pt-12 pb-10 rounded-2xl shadow bg-gradient-to-br text-white',
          tag === 'left'
            ? 'from-[rgba(55,51,255,1)] to-[rgba(159,84,254,1)]'
            : 'from-[rgba(64,209,255,1)] to-[rgba(62,81,255,1)]',
          className,
        )}
      >
        <div className='flex-1'>
          <h3
            style={{ textShadow: '0px 0px 9px rgba(0, 0, 0, 0.35)' }}
            className='text-5xl font-black leading-[47px] text-center hidden lg:block uppercase'
          >
            {title?.en}
          </h3>

          <div className='mt-2.5 flex xl:flex-row items-center justify-center space-x-3 xl:space-x-2 text-center'>
            <div>
              <p className='text-2xl font-bold lg:text-3xl'>{remainingTime?.days}</p>
              <p className='text-xs lg:text-base font-bold'>{t('Days')}</p>
            </div>

            <p className='font-bold text-xl lg:text-2xl'>:</p>

            <div>
              <p className='text-2xl font-bold lg:text-3xl'>{remainingTime?.hours}</p>
              <p className='text-xs  font-normal lg:text-base lg:font-medium'>{t('Hours')}</p>
            </div>

            <p className='font-bold text-xl lg:text-2xl'>:</p>

            <div>
              <p className='text-2xl font-bold lg:text-3xl'>{remainingTime?.minutes}</p>
              <p className='text-xs  font-normal lg:text-base lg:font-medium'>{t('Minutes')}</p>
            </div>
          </div>

          <div className='flex items-center justify-center space-x-2 mt-2'>
            <div className='flex flex-col items-center'>
              <div className='text-[10px] font-bold'>{t('Ticket Used')}</div>
              <div className='w-14 h-7 flex items-center justify-center rounded-xl border border-white text-sm font-bold'>
                {ticketUsed}
              </div>
            </div>

            <div className='flex flex-col items-center'>
              <div className='text-[10px] font-bold'>{t('Participants')}</div>
              <div className='w-14 h-7 flex items-center justify-center rounded-xl border border-white text-sm font-bold'>
                {participantCount}
              </div>
            </div>
          </div>
        </div>

        <div className='flex-1 flex flex-col items-center'>
          <div className='w-full flex flex-col items-center justify-center px-4 py-3 bg-[rgba(46,46,46,0.30)] rounded border border-white'>
            <div
              style={{
                textShadow: tag === 'left' ? '0px 0px 7px #FFDA68' : '0px 0px 7px #52C1FF',
              }}
              className='text-2xl font-medium'
            >
              {t('Reward')}
            </div>
            <div
              style={{
                textShadow:
                  tag === 'left'
                    ? '0px 2px 1px rgba(0, 0, 0, 0.30), 0px 4px 7px #FFDA68'
                    : '0px 2px 1px rgba(0, 0, 0, 0.30), 0px 4px 7px #9BDBFF',
              }}
              className='text-4xl font-black flex'
            >
              {totalReward || 1000} {rewardToken || 'USDT'}
            </div>
          </div>

          {!usage ? (
            <button
              className={classNames(
                'w-full mt-6 px-4 py-2 lg:h-14 rounded-lg flex items-center justify-center',
                isAvailable ? 'bg-gray-500 text-gray-300' : 'bg-pink-600',
              )}
              onClick={() => showModal(isAuthenticated ? id : 'login')}
              {...(isAvailable ? { disabled: true } : {})}
            >
              <span className='text-base lg:font-bold lg:text-xl font-bold'>{t('Enter Now')}</span>
              <i className='icon-fire text-2xl ml-3' />
            </button>
          ) : (
            <div className='mt-6 grow flex items-center justify-center'>
              <div className='text-center text-lg drop-shadow-amber-50'>
                {t('Successfully joined with {{usage}} tickets.', { usage })}
              </div>
            </div>
          )}

          <div className='mt-2 text-[10px] font-medium'>
            {t('The more tickets you deposit, the higher your chances.')}
          </div>
        </div>
      </div>

      <Modal id={id} className='border border-[#655A38]'>
        <div className='flex flex-col items-center'>
          <div className='text-2xl font-semibold text-blue-500 dark:text-[#FFDA68] text-center'>
            {t('WEEKLY GIVEAWAY')}
          </div>
          <div
            className={classNames(
              'rounded-t-xl w-[520px] h-40 overflow-hidden mt-4 border-b',
              tag === 'left' ? 'border-[#FFDA68]/80 dark:border-[#655A38]' : 'border-[#536c9e]',
            )}
            style={{
              backgroundImage: `url(${
                tag === 'left' ? '/giveaway-left.png' : '/giveaway-right.png'
              })`,
            }}
          >
            <div className='w-full h-full bg-white/50 dark:bg-[#242424]/80 flex flex-col items-center'>
              <div className='mt-9 flex items-center'>
                <button
                  className='w-10 h-10 rounded-full text-3xl flex items-center justify-center dark:text-[#FFDA68] border dark:border-[#FFDA68] border-black disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={() => setTickets((tic) => tic - 1)}
                  disabled={tickets === 1}
                >
                  -
                </button>

                <input
                  className='text-[26px] font-medium px-2 pb-1 mx-2 border-b dark:border-white border-black bg-transparent outline-none border-transparent w-12 text-center'
                  value={tickets}
                  onChange={(e) => {
                    if (
                      !isNaN(Number(e.target.value)) &&
                      Number(e.target.value) >= 0 &&
                      Number(e.target.value) <= profile.currentTicket
                    ) {
                      setTickets(Number(e.target.value));
                    }
                  }}
                />

                <button
                  className='w-10 h-10 rounded-full text-3xl flex items-center justify-center dark:text-[#FFDA68] border border-black dark:border-[#FFDA68] disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={() => setTickets((tic) => tic + 1)}
                  disabled={tickets === profile.currentTicket}
                >
                  +
                </button>

                <button
                  onClick={() => setTickets(profile.currentTicket)}
                  className='flex ml-4 dark:text-[#FFDA68]'
                >
                  <div>Max</div>
                  <i className='icon-arrow-left mt-1 rotate-90' />
                </button>
              </div>

              <div className='mt-4 dark:text-[#FFDA68]'>
                {t('{{percentage}}% chance of winning', {
                  percentage: ((tickets * 100) / (ticketUsed + tickets)).toString().slice(0, 4),
                  // percentage: ((tickets * 100) / (2 + tickets)).toString().slice(0, 4),
                  // In this example, we'll be giving total of 3 tokens and one of them will be ours
                })}
              </div>

              <div className='flex items-center mt-2'>
                <i className='icon-ticket text-blue-500 dark:text-[#FFDA68]' />
                <div className='text-xs ml-1 mr-2'>
                  {t('Usable Tickets:')}{' '}
                  <span className='text-blue-500 dark:text-[#FFDA68] font-bold'>
                    {profile.currentTicket}
                  </span>
                </div>

                <TicketInGiveawayIcon />

                <div className='text-xs ml-1'>
                  {t('Your tickets in this giveaway:')}{' '}
                  <span className='text-blue-500 dark:text-[#FFDA68] font-bold'>0</span>
                </div>
              </div>
            </div>
          </div>
          <div className='mt-5 text-center text-sm'>
            {t('The more tickets you deposit, the higher your chances.')}
          </div>

          <div className='w-full rounded-xl border border-blue-500 dark:border-[#FFDA68] dark:text-[#FFDA68] flex flex-col items-center pt-2 pb-3 mt-5'>
            <div className='text-xl font-semibold'>{t('Remaining Time')}</div>
            <div className='flex space-x-2'>
              <div className='flex flex-col items-center'>
                <div className='text-[40px] font-semibold'>{remainingTime.days}</div>
                <div>{t('Days')}</div>
              </div>

              <div className='text-[40px] font-semibold'>:</div>

              <div className='flex flex-col items-center'>
                <div className='text-[40px] font-semibold'>{remainingTime.hours}</div>
                <div>{t('Hours')}</div>
              </div>

              <div className='text-[40px] font-semibold'>:</div>

              <div className='flex flex-col items-center'>
                <div className='text-[40px] font-semibold'>{remainingTime.minutes}</div>
                <div>{t('Minutes')}</div>
              </div>
            </div>
          </div>

          <button
            onClick={handleEnter}
            className='rounded-lg bg-blue-500 dark:bg-[#FFDA68] text-white dark:text-[#151515] text-sm font-medium px-12 py-2.5 mt-5'
          >
            {t('Enter Now!')}
          </button>
        </div>
      </Modal>
    </>
  );
}
